import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type Lang = 'pt' | 'en'

// Ordem dos idiomas no seletor. Para adicionar espanhol/francês, basta
// criar o dicionário e incluir a sigla aqui.
export const LANGS: { code: Lang; label: string }[] = [
  { code: 'pt', label: 'PT' },
  { code: 'en', label: 'EN' },
]

type Dict = Record<string, any>

const pt: Dict = {
  nav: { inicio: 'Início', servicos: 'Serviços', sobre: 'Sobre', contato: 'Fale conosco' },
  hero: {
    eyebrow: 'Consultoria para hospitalidade',
    titlePre: 'Clientes felizes,',
    titleMid: 'sua conta',
    titleAccent: 'no azul',
    sub: 'Ajudamos restaurantes, padarias, cafés, hotéis e negócios de turismo a atender melhor e gastar melhor, com um método simples e sem enrolação.',
    ctaPrimary: 'Agende uma conversa',
    ctaSecondary: 'Ver serviços',
    badgeRating: 'clientes satisfeitos',
    badgeCount: 'negócios atendidos',
  },
  stats: [
    { valor: '+100', label: 'negócios atendidos' },
    { valor: '5', label: 'países atendidos' },
    { valor: '10 anos', label: 'de operação e números' },
  ],
  segments: {
    eyebrow: 'Para quem',
    title: 'Feito para a hospitalidade inteira',
    items: [
      { emoji: '🍽️', label: 'Restaurantes' },
      { emoji: '🍸', label: 'Bares' },
      { emoji: '☕', label: 'Cafés' },
      { emoji: '🥐', label: 'Padarias' },
      { emoji: '🏨', label: 'Hotéis' },
      { emoji: '🌴', label: 'Pousadas' },
      { emoji: '🧳', label: 'Turismo' },
    ],
  },
  services: {
    eyebrow: 'O que fazemos',
    title: 'Da operação à planilha, a gente cuida',
    link: 'Saiba mais',
    items: [
      { emoji: '🛎️', titulo: 'Operação & Atendimento', resumo: 'Dos bastidores ao atendimento, tudo rodando redondo.' },
      { emoji: '📊', titulo: 'Custos & Margem', resumo: 'Custos sob controle e preço que fecha a conta.' },
      { emoji: '🌍', titulo: 'Expansão', resumo: 'Abra a próxima unidade sem sufoco.' },
    ],
  },
  feature: {
    eyebrow: 'Jeito IMB',
    title: 'A gente trabalha lado a lado com você',
    text: 'Nada de fórmula pronta. Entendemos o seu negócio de dentro, ajustamos o que pesa no bolso e deixamos tudo redondo, do primeiro contato ao pós-venda.',
    pontos: [
      'Diagnóstico no seu negócio, não no PowerPoint',
      'Preço que respeita a sua margem',
      'Equipe treinada pra rodar sem você por perto',
    ],
  },
  brands: {
    title: 'Quem já confia na IMB',
    subtitle: 'Negócios de hospitalidade que contam com a gente',
  },
  cta: {
    title: 'Bora deixar seu negócio redondo?',
    text: 'Um papo de 30 minutos já mostra por onde começar. Sem compromisso, sem juridiquês.',
    button: 'Agende uma conversa',
  },
  footer: {
    tagline: 'Consultoria para hospitalidade: gastronomia, hotelaria e turismo.',
    email: 'contato@imbconsultoria.com.br',
    phone: '+353 83 349 9129',
    local: 'Irlanda · Brasil · Atendimento internacional',
    cnpj: 'CNPJ: 31.951.885/0001-38',
  },
  contato: {
    eyebrow: 'Fale conosco',
    title: 'Vamos conversar sobre o seu negócio',
    sub: 'Preencha os campos abaixo e a gente responde rapidinho.',
    nome: 'Seu nome',
    email: 'Seu e-mail',
    assunto: 'Assunto',
    mensagem: 'Sua mensagem',
    enviar: 'Enviar mensagem',
    sucesso: 'Tudo certo! Abrimos seu e-mail com a mensagem pronta pra enviar.',
    erroNome: 'Escreva seu nome.',
    erroEmail: 'Escreva um e-mail válido.',
    erroMensagem: 'Escreva sua mensagem.',
    direto: 'Prefere ir direto? Escreva para',
  },
  servicos: {
    eyebrow: 'Nossos serviços',
    title: 'Tudo que o seu negócio precisa, em ordem',
    sub: 'Do atendimento ao caixa, cada frente tem um plano claro e um responsável. Escolha por onde começar, a gente cuida do resto.',
    items: [
      {
        emoji: '🛎️',
        titulo: 'Operação & Atendimento',
        texto: 'Consultoria de gestão para restaurantes, bares, cafés, padarias e hospedagem. Colocamos a operação em ordem, dos bastidores ao atendimento, para melhorar a experiência do cliente e o resultado do negócio.',
        casos: [
          'Padronização de processos e rotinas',
          'Gestão de equipe e escalas',
          'Controle de estoque e fornecedores',
          'Experiência do cliente e do hóspede',
          'Cardápio e mix de produtos',
          'Indicadores do dia a dia',
        ],
      },
      {
        emoji: '🏨',
        titulo: 'Hotelaria & Turismo',
        texto: 'Consultoria para hotéis, pousadas e negócios de turismo. Ajudamos a definir posicionamento, estruturar a jornada do hóspede e ampliar ocupação e canais de venda, com uma proposta clara e objetiva.',
        casos: [
          'Posicionamento e proposta de valor',
          'Jornada e experiência do hóspede',
          'Gestão de ocupação e reservas',
          'Canais de venda e parcerias',
          'Reputação e avaliações online',
          'Atendimento internacional',
        ],
      },
      {
        emoji: '🌍',
        titulo: 'Expansão Internacional',
        texto: 'Consultoria para quem quer crescer além das fronteiras. Com experiência prática na Irlanda e em diversos países, apoiamos a entrada em novos mercados de forma simples e estruturada.',
        casos: [
          'Estudo do mercado de destino',
          'Adaptação do modelo de negócio',
          'Planejamento de abertura',
          'Estrutura operacional local',
          'Boas práticas internacionais',
          'Acompanhamento na implantação',
        ],
      },
      {
        emoji: '💶',
        titulo: 'Finanças & Custos',
        texto: 'Consultoria financeira feita para a hospitalidade. Colocamos custos, precificação e margem sob controle para que o negócio pare de operar no escuro e passe a decidir com números.',
        casos: [
          'Controle de custos e CMV',
          'Precificação e ticket médio',
          'Análise de margem por produto',
          'Redução de desperdício e perdas',
          'Fluxo de caixa e capital de giro',
          'Metas e acompanhamento de resultados',
        ],
      },
    ],
  },
  sobre: {
    heroTitle: 'Hospitalidade que vira resultado',
    heroSub: 'Consultoria empresarial para gastronomia, hotelaria e turismo, com mais de 100 empresas atendidas na Irlanda e no mundo.',
    trajTitle: 'Nossa Trajetória',
    traj1: 'A IMB Consultoria nasceu para simplificar a gestão de quem vive da hospitalidade: restaurantes, bares, cafés, padarias, hotéis e pousadas. São mais de 100 empresas atendidas, com atuação na Irlanda e em projetos internacionais no Brasil, Espanha, França, Colômbia e Argentina.',
    traj2: 'Nossa proposta é direta: método simples, linguagem objetiva e foco no que muda o resultado do negócio. Nada de relatório que ninguém lê. Entregamos passos claros e acompanhamos a implantação de perto.',
    values: [
      { h: 'Simplicidade', p: 'Método objetivo, sem jargão. O dono entende, aplica e vê o efeito.' },
      { h: 'Experiência', p: 'Mais de 100 empresas atendidas, da operação local ao mercado internacional.' },
      { h: 'Resultado', p: 'Foco em custo, margem e experiência do cliente, o que sustenta o negócio.' },
    ],
    ctaTitle: 'Pronto para organizar o seu negócio?',
    ctaButton: 'Fale com a IMB',
  },
}

const en: Dict = {
  nav: { inicio: 'Home', servicos: 'Services', sobre: 'About', contato: 'Get in touch' },
  hero: {
    eyebrow: 'Consulting for hospitality',
    titlePre: 'Happy customers,',
    titleMid: 'your books',
    titleAccent: 'in the black',
    sub: 'We help restaurants, bakeries, cafes, hotels and tourism businesses serve better and spend better, with a simple, no nonsense method.',
    ctaPrimary: 'Book a chat',
    ctaSecondary: 'See services',
    badgeRating: 'happy clients',
    badgeCount: 'businesses served',
  },
  stats: [
    { valor: '+100', label: 'businesses served' },
    { valor: '5', label: 'countries served' },
    { valor: '10 years', label: 'of operations and numbers' },
  ],
  segments: {
    eyebrow: 'Who we serve',
    title: 'Made for all of hospitality',
    items: [
      { emoji: '🍽️', label: 'Restaurants' },
      { emoji: '🍸', label: 'Bars' },
      { emoji: '☕', label: 'Cafes' },
      { emoji: '🥐', label: 'Bakeries' },
      { emoji: '🏨', label: 'Hotels' },
      { emoji: '🌴', label: 'Guesthouses' },
      { emoji: '🧳', label: 'Tourism' },
    ],
  },
  services: {
    eyebrow: 'What we do',
    title: 'From operations to the spreadsheet, we handle it',
    link: 'Learn more',
    items: [
      { emoji: '🛎️', titulo: 'Operations & Service', resumo: 'From back office to front desk, everything running smoothly.' },
      { emoji: '📊', titulo: 'Costs & Margin', resumo: 'Costs under control and prices that add up.' },
      { emoji: '🌍', titulo: 'Expansion', resumo: 'Open your next location without the headache.' },
    ],
  },
  feature: {
    eyebrow: 'The IMB way',
    title: 'We work side by side with you',
    text: 'No one size fits all. We get to know your business from the inside, fix what hurts the bottom line and leave everything running, from the first hello to the follow up.',
    pontos: [
      'Diagnosis on site, not in a slide deck',
      'Pricing that respects your margin',
      'A team trained to run without you around',
    ],
  },
  brands: {
    title: 'Businesses that trust IMB',
    subtitle: 'Hospitality businesses that count on us',
  },
  cta: {
    title: 'Ready to get your business running smoothly?',
    text: 'A 30 minute chat already shows where to start. No strings, no legalese.',
    button: 'Book a chat',
  },
  footer: {
    tagline: 'Consulting for hospitality: food, lodging and tourism.',
    email: 'contato@imbconsultoria.com.br',
    phone: '+353 83 349 9129',
    local: 'Ireland · Brazil · International',
    cnpj: 'CNPJ: 31.951.885/0001-38',
  },
  contato: {
    eyebrow: 'Get in touch',
    title: 'Let us talk about your business',
    sub: 'Fill in the fields below and we get back to you quickly.',
    nome: 'Your name',
    email: 'Your email',
    assunto: 'Subject',
    mensagem: 'Your message',
    enviar: 'Send message',
    sucesso: 'All set! We opened your email with the message ready to send.',
    erroNome: 'Please enter your name.',
    erroEmail: 'Please enter a valid email.',
    erroMensagem: 'Please write your message.',
    direto: 'Rather go direct? Write to',
  },
  servicos: {
    eyebrow: 'Our services',
    title: 'Everything your business needs, in order',
    sub: 'From service to the register, each area has a clear plan and an owner. Pick where to start, we take care of the rest.',
    items: [
      {
        emoji: '🛎️',
        titulo: 'Operations & Service',
        texto: 'Management consulting for restaurants, bars, cafes, bakeries and lodging. We put operations in order, from back office to front desk, to improve the guest experience and the bottom line.',
        casos: [
          'Standardized processes and routines',
          'Team and shift management',
          'Stock and supplier control',
          'Guest and customer experience',
          'Menu and product mix',
          'Day to day metrics',
        ],
      },
      {
        emoji: '🏨',
        titulo: 'Hospitality & Tourism',
        texto: 'Consulting for hotels, guesthouses and tourism businesses. We help define positioning, shape the guest journey and grow occupancy and sales channels, with a clear, focused proposition.',
        casos: [
          'Positioning and value proposition',
          'Guest journey and experience',
          'Occupancy and bookings',
          'Sales channels and partnerships',
          'Reputation and online reviews',
          'International guests',
        ],
      },
      {
        emoji: '🌍',
        titulo: 'International Expansion',
        texto: 'Consulting for those who want to grow beyond borders. With hands on experience in Ireland and several countries, we support entering new markets in a simple, structured way.',
        casos: [
          'Target market study',
          'Business model adaptation',
          'Opening planning',
          'Local operational setup',
          'International best practices',
          'Support through rollout',
        ],
      },
      {
        emoji: '💶',
        titulo: 'Finance & Costs',
        texto: 'Financial consulting built for hospitality. We put costs, pricing and margin under control so the business stops running blind and starts deciding with numbers.',
        casos: [
          'Cost and COGS control',
          'Pricing and average ticket',
          'Margin analysis per product',
          'Waste and loss reduction',
          'Cash flow and working capital',
          'Targets and results tracking',
        ],
      },
    ],
  },
  sobre: {
    heroTitle: 'Hospitality that turns into results',
    heroSub: 'Business consulting for food, lodging and tourism, with more than 100 businesses served in Ireland and around the world.',
    trajTitle: 'Our Story',
    traj1: 'IMB Consultoria was born to simplify management for those who live off hospitality: restaurants, bars, cafes, bakeries, hotels and guesthouses. More than 100 businesses served, with work in Ireland and international projects in Brazil, Spain, France, Colombia and Argentina.',
    traj2: 'Our approach is straightforward: a simple method, plain language and focus on what moves the result. No reports nobody reads. We deliver clear steps and follow the rollout closely.',
    values: [
      { h: 'Simplicity', p: 'A clear method, no jargon. The owner understands it, applies it and sees the effect.' },
      { h: 'Experience', p: 'More than 100 businesses served, from local operations to international markets.' },
      { h: 'Results', p: 'Focus on cost, margin and guest experience, what keeps the business alive.' },
    ],
    ctaTitle: 'Ready to get your business organized?',
    ctaButton: 'Talk to IMB',
  },
}

const dicts: Record<Lang, Dict> = { pt, en }

function detectLang(): Lang {
  try {
    const saved = localStorage.getItem('lang') as Lang | null
    if (saved && dicts[saved]) return saved
  } catch (e) {
    /* localStorage indisponível */
  }
  const nav = (navigator.language || 'pt').toLowerCase()
  if (nav.startsWith('en')) return 'en'
  return 'pt'
}

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (key: string) => any }

const I18nContext = createContext<Ctx>({ lang: 'pt', setLang: () => {}, t: (k) => k })

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>('pt')

  // Detecção automática só no cliente, depois da montagem.
  useEffect(() => {
    setLangState(detectLang())
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang === 'en' ? 'en' : 'pt-BR'
  }, [lang])

  const setLang = useCallback((l: Lang) => {
    try {
      localStorage.setItem('lang', l)
    } catch (e) {
      /* ignore */
    }
    setLangState(l)
  }, [])

  const t = useCallback(
    (key: string) => {
      const path = key.split('.')
      const read = (d: Dict) => path.reduce<any>((acc, p) => (acc == null ? acc : acc[p]), d)
      const value = read(dicts[lang])
      return value !== undefined && value !== null ? value : read(dicts.pt) ?? key
    },
    [lang],
  )

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => useContext(I18nContext)
